import { Link, matchPath, useLocation, useParams, Outlet, useOutletContext } from 'react-router';
import { useGroupEntity } from '@group/hooks';
import { FullScreenLoading } from '@framework/ui';
import { NotFound } from '@framework/ui/pages/NotFound';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-regular-svg-icons';
import { useCurrentUserId } from '@framework/auth';
import { UserIconMenu } from '@framework/ui/elements/TopNavigation';
import type { GroupId } from 'balus-common';
import { classNames } from '@framework/utils';
import type { GroupEntity } from '@group/domain';
import { useGroupMyMemberRole } from '@group/hooks/useGroupMyMemberRole';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { RTDBPath } from 'balus-common';
import { useGroupLicensePermissions, WithGroupLicensePermissions } from '@group/License';

// 複数パスのマッチングを簡潔に行うヘルパー関数（matchPath は end オプションで完全一致を指定）
const matchAnyPath = (paths: string[], pathname: string): boolean =>
  paths.some((path) => matchPath({ path, end: true }, pathname) !== null);

type LinkProps = {
  to: string;
  title: string;
  isCurrentPage: boolean;
};

const NavigationLink: React.FC<LinkProps> = ({ to, title, isCurrentPage }: LinkProps) => {
  return (
    <Link
      to={to}
      className={classNames(
        'flex-0 mb-1 flex items-center rounded-sm py-1 pl-2 hover:bg-gray-400',
        isCurrentPage ? 'bg-gray-300' : ''
      )}
    >
      {title}
    </Link>
  );
};

type NavProps = {
  groupId: GroupId;
};

const GroupConsoleSideNavigation: React.FC<NavProps> = ({ groupId }: NavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const permissions = useGroupLicensePermissions();

  const {
    isGroupConsolePage,
    isGroupConsoleLicensePage,
    isGroupConsoleMembersPage,
    isGroupConsoleInvitationsPage,
    isGroupConsoleInvitePage,
    isGroupConsoleIpRestrictionPage,
  } = useMemo(() => {
    return {
      isGroupConsolePage: matchAnyPath([UserPagePathBuilder.groupConsolePage(':groupId')], location.pathname),
      isGroupConsoleLicensePage: matchAnyPath(
        [UserPagePathBuilder.groupConsoleLicensePage(':groupId')],
        location.pathname
      ),
      isGroupConsoleMembersPage: matchAnyPath(
        [
          UserPagePathBuilder.groupConsoleMembersPage(':groupId'),
          UserPagePathBuilder.groupConsoleMemberPage(':groupId', ':memberId'),
        ],
        location.pathname
      ),
      isGroupConsoleInvitationsPage: matchAnyPath(
        [UserPagePathBuilder.groupConsoleInvitationsPage(':groupId')],
        location.pathname
      ),
      isGroupConsoleInvitePage: matchAnyPath(
        [UserPagePathBuilder.groupConsoleInvitePage(':groupId')],
        location.pathname
      ),
      isGroupConsoleIpRestrictionPage: matchAnyPath(
        [UserPagePathBuilder.groupConsoleIpRestrictionPage(':groupId')],
        location.pathname
      ),
    };
  }, [location]);

  const [isEnabledIpRestrictionFeature] = useSnapshot<boolean>({
    path: RTDBPath.Group.ipRestrictionEnabledPath(groupId),
    load({ snapshot }) {
      return !!snapshot.val();
    },
  });

  return (
    <>
      <NavigationLink
        to={UserPagePathBuilder.groupConsolePage(groupId)}
        title={t('group.console.nav.settings')}
        isCurrentPage={isGroupConsolePage}
      />
      <NavigationLink
        to={UserPagePathBuilder.groupConsoleLicensePage(groupId)}
        title={t('group.console.nav.license')}
        isCurrentPage={isGroupConsoleLicensePage}
      />
      <NavigationLink
        to={UserPagePathBuilder.groupConsoleMembersPage(groupId)}
        title={t('group.members.label')}
        isCurrentPage={isGroupConsoleMembersPage}
      />
      <NavigationLink
        to={UserPagePathBuilder.groupConsoleInvitePage(groupId)}
        title={t('group.console.nav.invite')}
        isCurrentPage={isGroupConsoleInvitePage}
      />
      <NavigationLink
        to={UserPagePathBuilder.groupConsoleInvitationsPage(groupId)}
        title={t('group.console.nav.inviteList')}
        isCurrentPage={isGroupConsoleInvitationsPage}
      />
      {isEnabledIpRestrictionFeature && permissions?.ipRestrictionCheck && (
        <NavigationLink
          to={UserPagePathBuilder.groupConsoleIpRestrictionPage(groupId)}
          title={t('group.console.nav.ipRestriction')}
          isCurrentPage={isGroupConsoleIpRestrictionPage}
        />
      )}
    </>
  );
};

// Outlet コンテキスト用の型定義
type GroupConsoleOutletContext = { group: GroupEntity };

// 子コンポーネントで group を取得するためのフック
export const useGroupConsoleOutletContext = () => useOutletContext<GroupConsoleOutletContext>();

export const WithGroupConsoleMenu: React.FC = () => {
  const { t } = useTranslation();
  const { groupId } = useParams<{ groupId: string }>();
  const { group, loading } = useGroupEntity(groupId ?? '');
  const currentUserId = useCurrentUserId();
  const isGroupAdmin = useGroupMyMemberRole(groupId ?? '') === 'admin';

  if (loading) return <FullScreenLoading />;

  // groupId が undefined または group が取得できない場合は NotFound を表示
  if (!groupId || !group) return <NotFound />;

  if (!isGroupAdmin)
    return (
      <NotFound
        message={t('group.console.notAdmin')}
        linkTo={UserPagePathBuilder.groupPage(group.id)}
        linkToMessage={t('common.label.backToGroup')}
      />
    );

  const outletContext: GroupConsoleOutletContext = { group };

  return (
    <WithGroupLicensePermissions>
      {/* ヘッダー */}
      <div className="h-16 px-4">
        <div className="flex w-full items-center border-b-2 px-8 py-4">
          <Link
            to={UserPagePathBuilder.groupPage(group.id)}
            className="mr-4 flex max-w-lg items-center truncate text-lg font-bold text-black hover:text-blue-900"
          >
            <span className="mr-4">
              {group.imageUrl ? (
                <span className="inline-block size-8 overflow-hidden rounded-full align-middle">
                  <img className="size-full object-cover" src={group.imageUrl} alt={group.name.toString()} />
                </span>
              ) : (
                <FontAwesomeIcon icon={faBuilding} className="text-brand" />
              )}
            </span>
            {group.name.toString()}
          </Link>

          <Link to={UserPagePathBuilder.groupPage(group.id)} className="mr-auto text-brand">
            {t('group.console.backLink')}
          </Link>

          <div>
            <UserIconMenu userId={currentUserId || ''} />
          </div>
        </div>
      </div>
      <div className="flex">
        {/* サイドメニュー */}
        <div className="mx-8 flex w-64 flex-col pt-4">
          <GroupConsoleSideNavigation groupId={groupId} />
        </div>
        {/* コンテンツ */}
        <div style={{ height: 'calc(100vh - 64px)' }} className="flex-1 overflow-y-scroll py-4 pr-4">
          <Outlet context={outletContext} />
        </div>
      </div>
    </WithGroupLicensePermissions>
  );
};
